<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.narrow.submit')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ $t("forwards.deals.narrow.title") }}</template>

    <template #body>
      <div v-if="slice" class="p-1">
        <p class="text-body-tertiary">
          {{
            $t("forwards.deals.narrow.body", {
              period: slice.period_label,
              mwh: formatNumberWithSpaces(slice.volume_mwh),
            })
          }}
        </p>

        <div v-if="widthOptions.length > 0">
          <span class="form-label d-block">{{
            $t("forwards.deals.narrow.width")
          }}</span>
          <SegmentedControl v-model="width" :options="widthOptions" />
        </div>
        <p v-else class="advisory-note mb-0">
          <FontAwesomeIcon icon="fa-regular fa-circle-info" />
          <span>{{ $t("forwards.deals.narrow.already_narrowest") }}</span>
        </p>

        <p class="form-text mt-2 mb-0">
          {{ $t("forwards.deals.narrow.hint") }}
        </p>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.deals.common.cancel") }}
      </button>
      <ActionButtonWithSpinner
        :busy="busy"
        :disabled="widthOptions.length === 0 || busy"
        variant="btn-primary"
        @click="emit('submit', width)"
      >
        {{ $t("forwards.deals.narrow.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { SliceResponse } from "@/forwards2/forwardsTypes";
import { parsePeriodLabel, type PeriodWidth } from "@/forwards2/periodUtils";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Break a slice's period into sub-periods, re-pinning its escrow onto the month each certificate
 * belongs to.
 *
 * Only widths narrower than the slice's own are offered; whether the backing pool can hold them is
 * the backend's call — its refusal names the pool and the width it lists at, which is more precise
 * than anything derivable here.
 */
const props = withDefaults(
  defineProps<{
    slice: SliceResponse | null;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { busy: false, errorMessage: null }
);

const emit = defineEmits<{
  submit: [width: PeriodWidth];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();

const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};
const width = ref<PeriodWidth>("month");

const currentWidth = computed<PeriodWidth | null>(
  () => parsePeriodLabel(props.slice?.period_label ?? "")?.width ?? null
);

const widthOptions = computed<SegmentedControlOption<PeriodWidth>[]>(() => {
  const narrower: PeriodWidth[] =
    currentWidth.value === "year"
      ? ["half_year", "quarter", "month"]
      : currentWidth.value === "half_year"
        ? ["quarter", "month"]
        : currentWidth.value === "quarter"
          ? ["month"]
          : [];
  return narrower.map((value) => ({
    value,
    label: t(`forwards.common.period_width.${value}`),
  }));
});

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) width.value = widthOptions.value[0]?.value ?? "month";
});

const close = () => {
  model.value = false;
};
</script>
